import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTenantContext } from '../auth/useTenantContext';

const API_BASE = (import.meta.env.VITE_API_BASE as string | undefined) ?? '/api/v1';

export interface PartialRow {
  pli_id: number;
  qty_required: number;
  qty_picked: number;
  row_status: string;
}

export class LifecycleApiError extends Error {
  status: number;
  code?: string;
  detail?: string;
  partialRows?: PartialRow[];
  constructor(status: number, code: string | undefined, detail: string | undefined, partialRows: PartialRow[] | undefined, message: string) {
    super(message);
    this.name = 'LifecycleApiError';
    this.status = status;
    this.code = code;
    this.detail = detail;
    this.partialRows = partialRows;
    // Restore prototype chain for instanceof checks
    Object.setPrototypeOf(this, LifecycleApiError.prototype);
  }
}

async function authedFetch(
  path: string,
  init: RequestInit,
  token: string,
  tenantCode: string,
  warehouseId: number | null = null,
): Promise<unknown> {
  const headers: Record<string, string> = {
    ...(init.headers as Record<string, string> ?? {}),
    Authorization: `Bearer ${token}`,
    'X-Tenant-Id': tenantCode,
  };
  if (warehouseId !== null) headers['X-Warehouse-Id'] = String(warehouseId);
  const res = await fetch(`${API_BASE}${path}`, { ...init, headers });
  if (!res.ok) {
    let body: { code?: string; detail?: string; partial_rows?: PartialRow[] } | null = null;
    try { body = await res.json() as { code?: string; detail?: string; partial_rows?: PartialRow[] }; } catch { /* non-JSON */ }
    throw new LifecycleApiError(res.status, body?.code, body?.detail, body?.partial_rows, `${res.status}: ${body?.code ?? 'error'}`);
  }
  if (res.status === 204) return null;
  return res.json();
}

function mutationHeaders(): HeadersInit {
  return { 'Content-Type': 'application/json', 'Idempotency-Key': crypto.randomUUID() };
}

/**
 * SP-8 — Scarica il CSV "inevasi" della pick-list e triggera il download
 * nel browser. Fetch authed (Bearer + X-Tenant-Id), risposta letta come
 * Blob, anchor sintetico + click + revokeObjectURL.
 *
 * Best-effort: ritorna true se il download e' stato avviato, false se il
 * server ha restituito un errore (logga in console — il caller mostra
 * comunque il toast di conferma).
 */
export async function downloadPartialsCsv(
  pickListId: number,
  token: string,
  tenantCode: string,
  warehouseId: number | null = null,
): Promise<boolean> {
  const headers: Record<string, string> = {
    Authorization: `Bearer ${token}`,
    'X-Tenant-Id': tenantCode,
  };
  if (warehouseId !== null) headers['X-Warehouse-Id'] = String(warehouseId);

  const res = await fetch(`${API_BASE}/admin/pick-lists/${pickListId}/partials.csv`, {
    method: 'GET',
    headers,
  });
  if (!res.ok) {
    console.error('downloadPartialsCsv failed', res.status, await res.text().catch(() => ''));
    return false;
  }

  const blob = await res.blob();
  const disposition = res.headers.get('Content-Disposition') ?? '';
  const m = /filename="?([^";]+)"?/i.exec(disposition);
  const filename = m?.[1] ?? `inevasi-${pickListId}.csv`;

  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
  return true;
}

/**
 * SP-8c — scarica il CSV "dettaglio" della pick-list (stampa capo magazzino).
 * Endpoint riservato a warehouse-admin; CSV Excel-friendly (BOM + `;` + CRLF).
 */
export async function downloadPickListCsv(
  pickListId: number,
  token: string,
  tenantCode: string,
  warehouseId: number | null = null,
): Promise<boolean> {
  const headers: Record<string, string> = {
    Authorization: `Bearer ${token}`,
    'X-Tenant-Id': tenantCode,
  };
  if (warehouseId !== null) headers['X-Warehouse-Id'] = String(warehouseId);

  const res = await fetch(`${API_BASE}/admin/pick-lists/${pickListId}/detail.csv`, {
    method: 'GET',
    headers,
  });
  if (!res.ok) {
    console.error('downloadPickListCsv failed', res.status, await res.text().catch(() => ''));
    return false;
  }

  const blob = await res.blob();
  const disposition = res.headers.get('Content-Disposition') ?? '';
  const m = /filename="?([^";]+)"?/i.exec(disposition);
  const filename = m?.[1] ?? `picklist-${pickListId}-dettaglio.csv`;

  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
  return true;
}

const detailKey  = (id: number) => ['admin', 'pick-lists', 'detail', id];
const listKey    = ['admin', 'pick-lists', 'list'] as const;
const auditKey   = ['admin', 'audit', 'tenant'] as const;
const countsKey  = (warehouseId: number) => ['admin', 'pick-lists', 'counts', warehouseId];
const boardKey   = (warehouseId: number) => ['admin', 'assignments', 'board', warehouseId];

function invalidateAll(qc: ReturnType<typeof useQueryClient>, pickListId: number, warehouseIdHint?: number): void {
  qc.invalidateQueries({ queryKey: detailKey(pickListId) });
  qc.invalidateQueries({ queryKey: listKey });
  qc.invalidateQueries({ queryKey: auditKey });
  if (warehouseIdHint !== undefined) {
    qc.invalidateQueries({ queryKey: countsKey(warehouseIdHint) });
    qc.invalidateQueries({ queryKey: boardKey(warehouseIdHint) });
  } else {
    qc.invalidateQueries({ queryKey: ['admin', 'pick-lists', 'counts'] });
    qc.invalidateQueries({ queryKey: ['admin', 'assignments', 'board'] });
  }
}

export function useConfirmPickList(token: string | null, tenantCode: string | null, warehouseIdHint?: number) {
  const qc = useQueryClient();
  const { warehouseId } = useTenantContext();
  return useMutation({
    mutationFn: async (vars: { pickListId: number; notes: string | null; forcePartial: boolean }) =>
      authedFetch(`/admin/pick-lists/${vars.pickListId}/confirm`, {
        method: 'POST',
        headers: mutationHeaders(),
        body: JSON.stringify({ notes: vars.notes, force_partial_accept: vars.forcePartial }),
      }, token!, tenantCode!, warehouseId),
    onSettled: (_data, _err, vars) => invalidateAll(qc, vars.pickListId, warehouseIdHint),
  });
}

export function useReopenPickList(token: string | null, tenantCode: string | null, warehouseIdHint?: number) {
  const qc = useQueryClient();
  const { warehouseId } = useTenantContext();
  return useMutation({
    mutationFn: async (vars: { pickListId: number }) =>
      authedFetch(`/admin/pick-lists/${vars.pickListId}/reopen`, {
        method: 'POST',
        headers: mutationHeaders(),
        body: JSON.stringify({}),
      }, token!, tenantCode!, warehouseId),
    onSettled: (_data, _err, vars) => invalidateAll(qc, vars.pickListId, warehouseIdHint),
  });
}

export function useEditItemPicked(token: string | null, tenantCode: string | null, warehouseIdHint?: number) {
  const qc = useQueryClient();
  const { warehouseId } = useTenantContext();
  return useMutation({
    mutationFn: async (vars: { pickListId: number; pliId: number; qtyPicked: number }) =>
      authedFetch(`/admin/pick-lists/${vars.pickListId}/items/${vars.pliId}/picked`, {
        method: 'PATCH',
        headers: mutationHeaders(),
        body: JSON.stringify({ qty_picked: vars.qtyPicked }),
      }, token!, tenantCode!, warehouseId),
    onSettled: (_data, _err, vars) => invalidateAll(qc, vars.pickListId, warehouseIdHint),
  });
}

/**
 * Registra colli e bancali su una lista (capo magazziniere), destinati alle
 * ultime due colonne del file di ritorno verso Book.
 *
 * Entrambi obbligatori: mandarne uno solo lascerebbe l'altro a `null`, cioè
 * "non compilato", e la lista resterebbe ferma senza partire verso l'ERP.
 *
 * `imballo` viaggia come TESTO ('Scatole' | 'Bancali'), non come codice: è la
 * stringa che legge Book. L'etichetta a schermo è per chi guarda, il valore è
 * per Book — se un domani l'etichetta cambiasse, il valore resterebbe questo.
 */
export function useSetColliImballo(token: string | null, tenantCode: string | null, warehouseIdHint?: number) {
  const qc = useQueryClient();
  const { warehouseId } = useTenantContext();
  return useMutation({
    mutationFn: async (vars: { pickListId: number; colli: number; imballo: string }) =>
      authedFetch(`/admin/pick-lists/${vars.pickListId}/colli-imballo`, {
        method: 'PATCH',
        headers: mutationHeaders(),
        body: JSON.stringify({ colli: vars.colli, imballo: vars.imballo }),
      }, token!, tenantCode!, warehouseId),
    onSettled: (_data, _err, vars) => invalidateAll(qc, vars.pickListId, warehouseIdHint),
  });
}

/**
 * Evasione manuale: chiude una lista in cui non c'è nulla da prelevare.
 *
 * Il vincolo "solo se non c'è nulla da prelevare" vive sul server: qui non si
 * ripete, si mostra soltanto il comando quando ha senso.
 */
export function useManualFulfill(token: string | null, tenantCode: string | null, warehouseIdHint?: number) {
  const qc = useQueryClient();
  const { warehouseId } = useTenantContext();
  return useMutation({
    mutationFn: async (pickListId: number) =>
      authedFetch(`/admin/pick-lists/${pickListId}/manual-fulfill`, {
        method: 'POST',
        headers: mutationHeaders(),
      }, token!, tenantCode!, warehouseId),
    onSettled: (_d, _e, pickListId) => invalidateAll(qc, pickListId, warehouseIdHint),
  });
}

export function useCancelMission(token: string | null, tenantCode: string | null, warehouseIdHint?: number) {
  const qc = useQueryClient();
  const { warehouseId } = useTenantContext();
  return useMutation({
    mutationFn: async (vars: { missionId: number; reason: string }) =>
      authedFetch(`/admin/missions/${vars.missionId}/cancel`, {
        method: 'POST',
        headers: mutationHeaders(),
        body: JSON.stringify({ reason: vars.reason }),
      }, token!, tenantCode!, warehouseId),
    onSettled: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'pick-lists'] });
      qc.invalidateQueries({ queryKey: auditKey });
      if (warehouseIdHint !== undefined) {
        qc.invalidateQueries({ queryKey: boardKey(warehouseIdHint) });
        qc.invalidateQueries({ queryKey: countsKey(warehouseIdHint) });
      } else {
        qc.invalidateQueries({ queryKey: ['admin', 'assignments', 'board'] });
      }
    },
  });
}

/**
 * SP-5p — reassign a mission to a different operator from the pick-list detail.
 * Uses the existing /reassign endpoint (works on pending/assigned/active: it
 * cancels the old mission and creates a successor for the new operator).
 */
export function useReassignMission(token: string | null, tenantCode: string | null, warehouseIdHint?: number) {
  const qc = useQueryClient();
  const { warehouseId } = useTenantContext();
  return useMutation({
    mutationFn: async (vars: { missionId: number; newOperatorUserId: number }) =>
      authedFetch(`/admin/missions/${vars.missionId}/reassign`, {
        method: 'POST',
        headers: mutationHeaders(),
        body: JSON.stringify({ new_operator_user_id: vars.newOperatorUserId }),
      }, token!, tenantCode!, warehouseId),
    onSettled: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'pick-lists'] });
      qc.invalidateQueries({ queryKey: auditKey });
      if (warehouseIdHint !== undefined) {
        qc.invalidateQueries({ queryKey: boardKey(warehouseIdHint) });
        qc.invalidateQueries({ queryKey: countsKey(warehouseIdHint) });
      } else {
        qc.invalidateQueries({ queryKey: ['admin', 'assignments', 'board'] });
      }
    },
  });
}

/**
 * SP-6f — "Ripristina lista": undo a split by deleting all missions+tasks of
 * an in_progress pick_list and reopening it to `open`. Refused server-side if
 * any mission is `active` or any task is `done` (the UI also gates these for
 * a cleaner UX). Returns `{ missions_deleted }`.
 */
export function useResetPickListSplit(token: string | null, tenantCode: string | null, warehouseIdHint?: number) {
  const qc = useQueryClient();
  const { warehouseId } = useTenantContext();
  return useMutation({
    mutationFn: async (vars: { pickListId: number }) =>
      authedFetch(`/admin/pick-lists/${vars.pickListId}/reset-split`, {
        method: 'POST',
        headers: mutationHeaders(),
        body: JSON.stringify({}),
      }, token!, tenantCode!, warehouseId),
    onSettled: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'pick-lists'] });
      qc.invalidateQueries({ queryKey: auditKey });
      if (warehouseIdHint !== undefined) {
        qc.invalidateQueries({ queryKey: boardKey(warehouseIdHint) });
        qc.invalidateQueries({ queryKey: countsKey(warehouseIdHint) });
      } else {
        qc.invalidateQueries({ queryKey: ['admin', 'assignments', 'board'] });
      }
    },
  });
}

/**
 * SP-6e — assign a pending (unassigned) mission to an operator from the
 * pick-list detail. Uses /assign (pending→assigned, same mission, no reason).
 * Complements the assignment board for the common "I just unassigned it and
 * want to put it back" case without leaving the detail page.
 */
export function useAssignMission(token: string | null, tenantCode: string | null, warehouseIdHint?: number) {
  const qc = useQueryClient();
  const { warehouseId } = useTenantContext();
  return useMutation({
    mutationFn: async (vars: { missionId: number; operatorUserId: number }) =>
      authedFetch(`/admin/missions/${vars.missionId}/assign`, {
        method: 'POST',
        headers: mutationHeaders(),
        body: JSON.stringify({ operator_user_id: vars.operatorUserId }),
      }, token!, tenantCode!, warehouseId),
    onSettled: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'pick-lists'] });
      qc.invalidateQueries({ queryKey: auditKey });
      if (warehouseIdHint !== undefined) {
        qc.invalidateQueries({ queryKey: boardKey(warehouseIdHint) });
        qc.invalidateQueries({ queryKey: countsKey(warehouseIdHint) });
      } else {
        qc.invalidateQueries({ queryKey: ['admin', 'assignments', 'board'] });
      }
    },
  });
}

/**
 * SP-11b — close (finalize) a mission that is in awaiting_close state.
 * Only valid on awaiting_close missions (backend rejects any other state).
 */
export function useCloseMission(token: string | null, tenantCode: string | null, warehouseIdHint?: number) {
  const qc = useQueryClient();
  const { warehouseId } = useTenantContext();
  return useMutation({
    mutationFn: async (vars: { missionId: number }) =>
      authedFetch(`/admin/missions/${vars.missionId}/close`, {
        method: 'POST',
        headers: mutationHeaders(),
        body: JSON.stringify({}),
      }, token!, tenantCode!, warehouseId),
    onSettled: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'pick-lists'] });
      qc.invalidateQueries({ queryKey: auditKey });
      if (warehouseIdHint !== undefined) {
        qc.invalidateQueries({ queryKey: boardKey(warehouseIdHint) });
        qc.invalidateQueries({ queryKey: countsKey(warehouseIdHint) });
      } else {
        qc.invalidateQueries({ queryKey: ['admin', 'assignments', 'board'] });
      }
    },
  });
}

/**
 * SP-5p — remove the operator from a mission (back to the unassigned pool).
 * Only valid on pending/assigned missions (backend rejects active/done/cancelled).
 */
export function useUnassignMission(token: string | null, tenantCode: string | null, warehouseIdHint?: number) {
  const qc = useQueryClient();
  const { warehouseId } = useTenantContext();
  return useMutation({
    mutationFn: async (vars: { missionId: number }) =>
      authedFetch(`/admin/missions/${vars.missionId}/unassign`, {
        method: 'POST',
        headers: mutationHeaders(),
      }, token!, tenantCode!, warehouseId),
    onSettled: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'pick-lists'] });
      qc.invalidateQueries({ queryKey: auditKey });
      if (warehouseIdHint !== undefined) {
        qc.invalidateQueries({ queryKey: boardKey(warehouseIdHint) });
        qc.invalidateQueries({ queryKey: countsKey(warehouseIdHint) });
      } else {
        qc.invalidateQueries({ queryKey: ['admin', 'assignments', 'board'] });
      }
    },
  });
}

export function useFulfilledCount(token: string | null, tenantCode: string | null, warehouseId: number | null) {
  // warehouseId === null is a valid case: tenant-admin memberships are
  // tenant-wide (no warehouse binding) and /me.warehouse comes back null.
  // In that case the backend aggregates counts across all warehouses of
  // the tenant, which is the meaningful tenant-total for the badge.
  return useQuery<number>({
    queryKey: countsKey(warehouseId ?? 0),
    enabled: token !== null && tenantCode !== null,
    refetchInterval: 30_000,
    queryFn: async () => {
      const path = warehouseId !== null
        ? `/admin/pick-lists/counts?warehouse_id=${warehouseId}`
        : `/admin/pick-lists/counts`;
      const data = await authedFetch(path, {}, token!, tenantCode!, warehouseId) as
        { by_status: { fulfilled: number } };
      return data.by_status.fulfilled;
    },
  });
}
